import React, { useCallback, useMemo, useState } from "react";

// internal deps
import { BottomSheet, BottomSheetChildrenProps, useEffectOnce } from "@swiggy-private/core-ui";

// components
import { SplitPayBanner } from "@payment-ui/components/split-pay-banner";

// helpers
import { getRewardsSplitpayList, getSplitpayRewardsByStatus } from "@payment-ui/helpers/SplitPay";
import { isEmpty } from "@swiggy-private/js-utils";

// types
import { SPLITPAY_SELECTION_TYPE, SplitpayAnalyticsEvents, SplitPayData } from "@payment-ui/interfaces";

// dls
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { default as ChevronDownIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronDown/12";

// constants
import { TEXTS } from "@payment-ui/constants";
import { SPLIT_PAY_REWARDS_HALF_CARD_TEST_IDS } from "@swiggy-private/payment-test-ids";

// styles
import {
    StyledCardContainer,
    Container,
    StyledHeading,
    IconWrapper,
    Icon,
    ViewUnavailableRewardsCtaContainer,
} from "./SplitpayRewardsHalfcard.styles";

interface ISplitPayRewardsHalfCardContainerProps {
    splitPayList: SplitPayData[];
    selectedSplitpayMethods?: string;
    handleVisibility: (show: boolean) => void;
    handleChange: (isSplitPayChecked: boolean, methodData: SplitPayData, removeReward?: boolean) => void;
    couponApplied?: boolean;
    rewardApplied?: boolean;
    analyticsEvents?: SplitpayAnalyticsEvents;
}

interface ISplitPayRewardsHalfCardContentProps extends ISplitPayRewardsHalfCardContainerProps {
    close: VoidFunction;
}

const SplitPayRewardsHalfCardContent = ({
    splitPayList,
    selectedSplitpayMethods,
    handleChange,
    close,
    couponApplied,
    rewardApplied,
    analyticsEvents,
}: ISplitPayRewardsHalfCardContentProps) => {
    const [showDisabledRewards, setShowDisabledRewards] = useState(false);
    const rewardsSplitpayList = getRewardsSplitpayList(splitPayList, couponApplied, rewardApplied);
    const [enabledSplitpayRewards, disabledSplitpayRewards] = getSplitpayRewardsByStatus(rewardsSplitpayList);

    const showViewUnavailableRewardsCTA = useMemo(
        () => disabledSplitpayRewards.length > 0 && !showDisabledRewards,
        [disabledSplitpayRewards, showDisabledRewards],
    );

    const handleViewUnavailableRewards = useCallback(() => {
        setShowDisabledRewards(true);
    }, []);

    const onHandleChange = useCallback(
        (isSplitPayChecked: boolean, methodData: SplitPayData) => {
            handleChange(isSplitPayChecked, methodData);
            close();
        },
        [close, handleChange],
    );

    useEffectOnce(() => {
        if (!isEmpty(analyticsEvents?.rewardsHalfCardImpressionEvent)) {
            analyticsEvents?.rewardsHalfCardImpressionEvent();
        }
    });

    return (
        <Container data-testid={SPLIT_PAY_REWARDS_HALF_CARD_TEST_IDS.CONTAINER}>
            <StyledHeading
                color="text_High_Emphasis"
                variant="V2.heading2.neutral.black"
                dangerouslySetInnerHTML={{ __html: TEXTS.YOUR_AVAILABLE_REWARDS }}
            />
            <StyledCardContainer cornerRadius="medium" elevation>
                {[...enabledSplitpayRewards, ...disabledSplitpayRewards].map((method) => {
                    if (!method.enabled && !showDisabledRewards) {
                        return null;
                    }

                    return (
                        <SplitPayBanner
                            data={method}
                            key={method.meta.rewardId}
                            handleChange={onHandleChange}
                            isSplitPayChecked={
                                selectedSplitpayMethods?.includes(method.paymentCode) ||
                                selectedSplitpayMethods?.includes(method?.splitPayIdentifier) ||
                                false
                            }
                            selectionType={SPLITPAY_SELECTION_TYPE.TICK}
                            analyticsEvents={analyticsEvents}
                        />
                    );
                })}
                {showViewUnavailableRewardsCTA && (
                    <ViewUnavailableRewardsCtaContainer>
                        <Text
                            variant="V2.body2.neutral.bold"
                            color="text_Button_Disabled"
                            onClick={handleViewUnavailableRewards}
                            dangerouslySetInnerHTML={{ __html: TEXTS.VIEW_UNAVAILABLE_REWARDS }}
                        />
                        <IconWrapper>
                            <Icon Icon={ChevronDownIcon} fillColor="text_Button_Disabled" />
                        </IconWrapper>
                    </ViewUnavailableRewardsCtaContainer>
                )}
            </StyledCardContainer>
        </Container>
    );
};

const SplitPayRewardsHalfCard = ({ handleVisibility, ...otherProps }: ISplitPayRewardsHalfCardContainerProps) => {
    const handleBottomSheetOnClose = useCallback(() => {
        if (!isEmpty(otherProps.analyticsEvents?.crossClickEvent)) {
            otherProps.analyticsEvents?.crossClickEvent();
        }
        handleVisibility(false);
    }, [handleVisibility, otherProps.analyticsEvents]);

    return (
        <BottomSheet hideOnOverlayClick onClose={handleBottomSheetOnClose}>
            {({ close }: BottomSheetChildrenProps): React.ReactElement => {
                return (
                    <SplitPayRewardsHalfCardContent close={close} {...otherProps} handleVisibility={handleVisibility} />
                );
            }}
        </BottomSheet>
    );
};

export default React.memo(SplitPayRewardsHalfCard);
